import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { LoaderCircleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/components/new/lib'
import { Button, ComboboxContent, ComboboxList as BaseComboboxList } from '@/components/new/ui'

import {
    areComboboxFieldOptionsEqual,
    DEFAULT_COMBOBOX_FIELD_LOAD_MORE_OFFSET,
    DEFAULT_COMBOBOX_FIELD_PAGE_SIZE,
    DEFAULT_COMBOBOX_FIELD_QUERY_DEBOUNCE_MS,
    getComboboxFieldValueIds,
    isAbortError,
    mergeUniqueComboboxFieldOptions,
    normalizeComboboxFieldValue,
    resolveHasMore,
} from './helpers'
import {
    ComboboxCollection,
    ComboboxItem,
    ComboboxStatus,
    GroupedComboboxCollection,
    VirtualizedComboboxCollection,
} from './ComboboxParts'
import { ComboboxFieldSelectionContext } from './selection-context'

import type { ComboboxFieldOption, ComboboxFieldProps } from './types'

const Combobox = ComboboxPrimitive.Root
const EMPTY_COMBOBOX_FIELD_OPTIONS: ComboboxFieldOption[] = []

export function ComboboxField(props: ComboboxFieldProps) {
    const { t } = useTranslation()
    const {
        open: openProp,
        defaultOpen = false,
        value: valueProp,
        defaultValue,
        searchValue,
        onValueChange,
        onInputValueChange: onInputValueChangeProp,
        options: optionsProp,
        asyncOptions,
        extraOptions,
        multiple = false,
        placeholder = t('components.control.combobox.placeholder'),
        searchPlaceholder,
        showSearchInput,
        triggerComponent,
        renderOption,
        groupBy,
        contentProps,
        listProps,
        loadingContent,
        loadingMoreContent,
        valueLoadingContent,
        emptyContent,
        actions,
        asyncQueryDebounceMs = DEFAULT_COMBOBOX_FIELD_QUERY_DEBOUNCE_MS,
        searchInputAutoFocus = true,
        queryOnOpen = true,
        reloadOptionsOnOpen = false,
        autoHighlight = true,
        onOpenChange: onOpenChangeProp,
        onOpenChangeComplete: onOpenChangeCompleteProp,
        onItemHighlighted: onItemHighlightedProp,
        disabled,
        virtualized = false,
        ...rootProps
    } = props

    const resolvedLoadingContent = loadingContent ?? t('components.control.combobox.loading')
    const resolvedLoadingMoreContent = loadingMoreContent ?? t('components.control.combobox.loadingMore')
    const resolvedValueLoadingContent = valueLoadingContent ?? resolvedLoadingContent
    const resolvedEmptyContent = emptyContent ?? t('components.control.combobox.noOptions')

    const options = optionsProp || EMPTY_COMBOBOX_FIELD_OPTIONS
    const isAsyncOptions = !!asyncOptions
    const isControlled = valueProp !== undefined
    const pageSize = DEFAULT_COMBOBOX_FIELD_PAGE_SIZE

    const [internalValue, setInternalValue] = useState<string[] | string | null>(() =>
        normalizeComboboxFieldValue(defaultValue, multiple)
    )
    const [selectedOptions, setSelectedOptions] = useState<ComboboxFieldOption[]>([])
    const [queriedOptions, setQueriedOptions] = useState<ComboboxFieldOption[]>([])
    const [knownTotalCount, setKnownTotalCount] = useState<number | null>(null)
    const [hasMore, setHasMore] = useState(true)
    const [isQuerying, setIsQuerying] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [isHydratingValue, setIsHydratingValue] = useState(false)
    const [localOpen, setOpen] = useState(defaultOpen)
    const open = openProp ?? localOpen
    const wasOpenRef = useRef(false)
    const [allLoadedAsyncOptions, setAllLoadedAsyncOptions] = useState<ComboboxFieldOption[] | null>(null)
    const [highlightedValue, setHighlightedValue] = useState<ComboboxFieldOption | undefined>(undefined)
    const [highlightedReason, setHighlightedReason] = useState<'keyboard' | 'pointer' | 'none' | undefined>(undefined)

    const queryAbortControllerRef = useRef<AbortController | null>(null)
    const hydrateAbortControllerRef = useRef<AbortController | null>(null)
    const queryDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const queryGenerationRef = useRef(0)
    const openGenerationRef = useRef(0)
    const lastInitialQueryCompletedOpenGenerationRef = useRef(0)
    const hydrateGenerationRef = useRef(0)
    const autoFillLoadedCountRef = useRef<number | null>(null)
    const lastHandledInputValueRef = useRef<string | null>(null)
    const hasLoadedInitialQueryRef = useRef(false)
    const queryValueRef = useRef('')
    const queriedOptionsRef = useRef<ComboboxFieldOption[]>(queriedOptions)
    const knownTotalCountRef = useRef<number | null>(knownTotalCount)
    const allLoadedAsyncOptionsRef = useRef<ComboboxFieldOption[] | null>(allLoadedAsyncOptions)
    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const listRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        queriedOptionsRef.current = queriedOptions
    }, [queriedOptions])

    useEffect(() => {
        knownTotalCountRef.current = knownTotalCount
    }, [knownTotalCount])

    useEffect(() => {
        allLoadedAsyncOptionsRef.current = allLoadedAsyncOptions
    }, [allLoadedAsyncOptions])

    const clearPendingQuery = useCallback(() => {
        if (queryDebounceTimerRef.current) {
            clearTimeout(queryDebounceTimerRef.current)
            queryDebounceTimerRef.current = null
        }
    }, [])

    const abortActiveQuery = useCallback(() => {
        queryAbortControllerRef.current?.abort()
        queryAbortControllerRef.current = null
    }, [])

    useEffect(() => {
        return () => {
            wasOpenRef.current = false
            hasLoadedInitialQueryRef.current = false
            clearPendingQuery()
            abortActiveQuery()
            hydrateAbortControllerRef.current?.abort()
        }
    }, [abortActiveQuery, clearPendingQuery])

    const normalizedValue = useMemo(
        () => normalizeComboboxFieldValue(isControlled ? valueProp : internalValue, multiple),
        [internalValue, isControlled, multiple, valueProp]
    )
    const valueIds = useMemo(() => getComboboxFieldValueIds(normalizedValue, multiple), [multiple, normalizedValue])
    const valueIdsKey = useMemo(() => valueIds.join('|'), [valueIds])
    const availableOptions = useMemo(
        () => mergeUniqueComboboxFieldOptions(options, extraOptions, queriedOptions, selectedOptions),
        [extraOptions, options, queriedOptions, selectedOptions]
    )
    const availableOptionMap = useMemo(() => {
        const result = new Map<string, ComboboxFieldOption>()
        availableOptions.forEach((option) => {
            result.set(option.value, option)
        })
        return result
    }, [availableOptions])
    const rootItems = useMemo(
        () =>
            isAsyncOptions
                ? mergeUniqueComboboxFieldOptions(options, extraOptions, queriedOptions, selectedOptions)
                : mergeUniqueComboboxFieldOptions(options, extraOptions, selectedOptions),
        [extraOptions, isAsyncOptions, options, queriedOptions, selectedOptions]
    )
    const canLoadMore = useMemo(() => {
        if (!isAsyncOptions) {
            return false
        }
        if (knownTotalCount !== null) {
            return queriedOptions.length < knownTotalCount
        }
        return hasMore
    }, [hasMore, isAsyncOptions, knownTotalCount, queriedOptions.length])

    const runQueryOptions = useCallback(
        (queryOptionsInput: { query: string; offset: number; append: boolean }) => {
            if (!asyncOptions) {
                return
            }

            const { query, offset, append } = queryOptionsInput
            if (!append) {
                queryGenerationRef.current += 1
            }
            const queryGeneration = queryGenerationRef.current
            queryValueRef.current = query

            clearPendingQuery()
            abortActiveQuery()

            const abortController = new AbortController()
            queryAbortControllerRef.current = abortController

            if (append) {
                setIsLoadingMore(true)
            } else {
                autoFillLoadedCountRef.current = null
                setIsQuerying(true)
                setIsLoadingMore(false)
                setQueriedOptions([])
                setKnownTotalCount(null)
                knownTotalCountRef.current = null
                setHasMore(true)

                if (query === '') {
                    setAllLoadedAsyncOptions(null)
                }
            }

            asyncOptions
                .queryOptions({
                    query,
                    offset,
                    limit: pageSize,
                    signal: abortController.signal,
                })
                .then((result) => {
                    if (queryGeneration !== queryGenerationRef.current) {
                        return
                    }

                    const loadedOptions = result.options || []
                    const nextOptions = append
                        ? mergeUniqueComboboxFieldOptions(queriedOptionsRef.current, loadedOptions)
                        : loadedOptions
                    const nextKnownTotalCount =
                        result.totalCount === undefined ? knownTotalCountRef.current : result.totalCount
                    const nextHasMore = resolveHasMore({
                        result,
                        loadedCount: loadedOptions.length,
                        currentCount: offset,
                        pageSize,
                        knownTotalCount: nextKnownTotalCount,
                    })

                    setQueriedOptions(nextOptions)
                    setKnownTotalCount(nextKnownTotalCount)
                    knownTotalCountRef.current = nextKnownTotalCount
                    setHasMore(nextHasMore)

                    if (query === '' && !nextHasMore) {
                        setAllLoadedAsyncOptions(nextOptions)
                    }

                    if (!append) {
                        lastInitialQueryCompletedOpenGenerationRef.current = openGenerationRef.current
                    }
                })
                .catch((error) => {
                    if (!isAbortError(error)) {
                        console.error(error)
                    }
                })
                .finally(() => {
                    if (queryGeneration === queryGenerationRef.current) {
                        if (append) {
                            setIsLoadingMore(false)
                        } else {
                            setIsQuerying(false)
                        }
                    }
                })
        },
        [abortActiveQuery, asyncOptions, clearPendingQuery, pageSize]
    )

    const queryOptions = useCallback(
        (query: string) => {
            if (!asyncOptions) {
                return
            }

            clearPendingQuery()
            abortActiveQuery()

            queryDebounceTimerRef.current = setTimeout(() => {
                queryDebounceTimerRef.current = null
                runQueryOptions({ query, offset: 0, append: false })
            }, asyncQueryDebounceMs)
        },
        [abortActiveQuery, asyncOptions, asyncQueryDebounceMs, clearPendingQuery, runQueryOptions]
    )

    const queryMoreOptions = useCallback(() => {
        if (!asyncOptions || !canLoadMore || isQuerying || isLoadingMore) {
            return
        }

        runQueryOptions({
            query: queryValueRef.current,
            offset: queriedOptionsRef.current.length,
            append: true,
        })
    }, [asyncOptions, canLoadMore, isLoadingMore, isQuerying, runQueryOptions])

    useEffect(() => {
        if (valueIds.length === 0) {
            hydrateGenerationRef.current += 1
            hydrateAbortControllerRef.current?.abort()
            setSelectedOptions((currentSelectedOptions) =>
                currentSelectedOptions.length === 0 ? currentSelectedOptions : []
            )
            setIsHydratingValue(false)
            return
        }

        const resolvedOptions = valueIds
            .map((valueId) => availableOptionMap.get(valueId))
            .filter((option): option is ComboboxFieldOption => !!option)
        const missingValueIds = valueIds.filter((valueId) => !availableOptionMap.has(valueId))

        setSelectedOptions((currentSelectedOptions) =>
            areComboboxFieldOptionsEqual(currentSelectedOptions, resolvedOptions)
                ? currentSelectedOptions
                : resolvedOptions
        )

        if (!isAsyncOptions || !asyncOptions?.loadOptions || missingValueIds.length === 0) {
            setIsHydratingValue(false)
            return
        }

        hydrateGenerationRef.current += 1
        const hydrateGeneration = hydrateGenerationRef.current
        hydrateAbortControllerRef.current?.abort()
        const abortController = new AbortController()
        hydrateAbortControllerRef.current = abortController
        setIsHydratingValue(true)

        asyncOptions
            .loadOptions({ values: missingValueIds, signal: abortController.signal })
            .then((result) => {
                if (hydrateGeneration !== hydrateGenerationRef.current) {
                    return
                }

                const hydratedOptions = result.filter((option): option is ComboboxFieldOption => !!option)
                if (hydratedOptions.length === 0) {
                    return
                }

                setQueriedOptions((currentOptions) => mergeUniqueComboboxFieldOptions(currentOptions, hydratedOptions))
                setSelectedOptions((currentSelectedOptions) => {
                    const mergedSelectedOptions = mergeUniqueComboboxFieldOptions(
                        currentSelectedOptions,
                        hydratedOptions
                    )
                    const selectedOptionMap = new Map<string, ComboboxFieldOption>()
                    mergedSelectedOptions.forEach((option) => {
                        selectedOptionMap.set(option.value, option)
                    })

                    return valueIds
                        .map((valueId) => selectedOptionMap.get(valueId))
                        .filter((option): option is ComboboxFieldOption => !!option)
                })
            })
            .catch((error) => {
                if (!isAbortError(error)) {
                    console.error(error)
                }
            })
            .finally(() => {
                if (hydrateGeneration === hydrateGenerationRef.current) {
                    setIsHydratingValue(false)
                }
            })
    }, [asyncOptions, availableOptionMap, isAsyncOptions, valueIds, valueIdsKey])

    const isDisabled = !!disabled || isHydratingValue

    const handleOpenChange = useCallback<
        NonNullable<ComboboxPrimitive.Root.Props<ComboboxFieldOption, boolean>['onOpenChange']>
    >(
        (nextOpen, eventDetails) => {
            setOpen(nextOpen)
            onOpenChangeProp?.(nextOpen, eventDetails)
        },
        [onOpenChangeProp]
    )

    useEffect(() => {
        if (wasOpenRef.current === open) {
            return
        }
        wasOpenRef.current = open

        if (!open) {
            clearPendingQuery()
            abortActiveQuery()
            return
        }

        openGenerationRef.current += 1
        if (!isAsyncOptions) {
            return
        }

        if (reloadOptionsOnOpen && queryOnOpen) {
            hasLoadedInitialQueryRef.current = true
            runQueryOptions({ query: queryValueRef.current, offset: 0, append: false })
            return
        }

        if (!hasLoadedInitialQueryRef.current) {
            hasLoadedInitialQueryRef.current = true
            if (allLoadedAsyncOptionsRef.current) {
                const cachedOptions = allLoadedAsyncOptionsRef.current
                setQueriedOptions(cachedOptions)
                setKnownTotalCount(cachedOptions.length)
                knownTotalCountRef.current = cachedOptions.length
                setHasMore(false)
                return
            }

            if (queryOnOpen) {
                runQueryOptions({ query: queryValueRef.current, offset: 0, append: false })
            }
        }
    }, [abortActiveQuery, clearPendingQuery, isAsyncOptions, open, queryOnOpen, reloadOptionsOnOpen, runQueryOptions])

    const handleOpenChangeComplete = useCallback(
        (nextOpen: boolean) => {
            onOpenChangeCompleteProp?.(nextOpen)
            const shouldRenderSearchInput = showSearchInput === undefined ? !!triggerComponent : showSearchInput
            if (!nextOpen || !shouldRenderSearchInput || !searchInputAutoFocus) {
                return
            }

            setTimeout(() => {
                searchInputRef.current?.focus({ preventScroll: true })
            }, 0)
        },
        [onOpenChangeCompleteProp, searchInputAutoFocus, showSearchInput, triggerComponent]
    )

    const handleInputValueChange = useCallback<
        NonNullable<ComboboxPrimitive.Root.Props<ComboboxFieldOption, boolean>['onInputValueChange']>
    >(
        (inputValue, eventDetails) => {
            onInputValueChangeProp?.(inputValue, eventDetails)

            if (eventDetails.reason !== 'input-change') {
                return
            }

            const isSameQueryValue = inputValue === queryValueRef.current
            lastHandledInputValueRef.current = inputValue
            if (!isAsyncOptions) {
                return
            }

            if (isSameQueryValue) {
                return
            }

            queryValueRef.current = inputValue
            hasLoadedInitialQueryRef.current = true
            const canSearchOnClient = !!allLoadedAsyncOptionsRef.current

            if (canSearchOnClient) {
                clearPendingQuery()
                abortActiveQuery()
                const nextOptions = allLoadedAsyncOptionsRef.current || []

                setQueriedOptions(nextOptions)
                setKnownTotalCount(nextOptions.length)
                knownTotalCountRef.current = nextOptions.length
                setHasMore(false)
                setIsQuerying(false)
                setIsLoadingMore(false)
                return
            }

            queryOptions(inputValue)
        },
        [abortActiveQuery, clearPendingQuery, isAsyncOptions, onInputValueChangeProp, queryOptions]
    )

    const handleItemHighlighted = useCallback<
        NonNullable<ComboboxPrimitive.Root.Props<ComboboxFieldOption, boolean>['onItemHighlighted']>
    >(
        (nextHighlightedValue, eventDetails) => {
            setHighlightedValue(nextHighlightedValue)
            setHighlightedReason(eventDetails.reason)
            onItemHighlightedProp?.(nextHighlightedValue, eventDetails)
        },
        [onItemHighlightedProp]
    )

    useEffect(() => {
        if (typeof searchValue !== 'string' || lastHandledInputValueRef.current === searchValue) {
            return
        }

        queryValueRef.current = searchValue

        if (!isAsyncOptions || !open) {
            return
        }

        hasLoadedInitialQueryRef.current = true

        const canSearchOnClient = !!allLoadedAsyncOptionsRef.current
        if (canSearchOnClient) {
            clearPendingQuery()
            abortActiveQuery()
            const nextOptions = allLoadedAsyncOptionsRef.current || []

            setQueriedOptions(nextOptions)
            setKnownTotalCount(nextOptions.length)
            knownTotalCountRef.current = nextOptions.length
            setHasMore(false)
            setIsQuerying(false)
            setIsLoadingMore(false)
            return
        }

        queryOptions(searchValue)
    }, [abortActiveQuery, allLoadedAsyncOptionsRef, clearPendingQuery, isAsyncOptions, open, queryOptions, searchValue])

    const handleValueChange = useCallback(
        (nextSelectedValue: ComboboxFieldOption | ComboboxFieldOption[] | null) => {
            const nextSelectedOptions = Array.isArray(nextSelectedValue)
                ? nextSelectedValue
                : nextSelectedValue
                  ? [nextSelectedValue]
                  : []

            const nextValue = multiple
                ? nextSelectedOptions.map((option) => option.value)
                : nextSelectedOptions[0]?.value || null

            setSelectedOptions(nextSelectedOptions)
            setQueriedOptions((currentOptions) => mergeUniqueComboboxFieldOptions(currentOptions, nextSelectedOptions))

            if (!isControlled) {
                setInternalValue(nextValue)
            }

            onValueChange?.(nextValue, multiple ? nextSelectedOptions : nextSelectedOptions[0] || null)
        },
        [isControlled, multiple, onValueChange]
    )

    const currentRootValue = multiple ? selectedOptions : selectedOptions[0] || null
    const shouldDeferItemsUntilOpen = virtualized && !!triggerComponent
    const resolvedRootItems = shouldDeferItemsUntilOpen && !open ? EMPTY_COMBOBOX_FIELD_OPTIONS : rootItems
    const selectedScrollValue = multiple
        ? selectedOptions[selectedOptions.length - 1]?.value
        : selectedOptions[0]?.value
    const inputLoadingPlaceholder =
        typeof resolvedValueLoadingContent === 'string'
            ? resolvedValueLoadingContent
            : t('components.control.combobox.loading')
    const inputPlaceholder = typeof placeholder === 'string' ? placeholder : undefined
    const resolvedSearchPlaceholder = searchPlaceholder || inputPlaceholder
    const shouldShowSearchInput = showSearchInput === undefined ? !!triggerComponent : showSearchInput
    const { className: contentClassName, ...restContentProps } = contentProps || {}
    const { className: listClassName, ...restListProps } = listProps || {}
    const resolvedContentClassName = cn(
        'flex max-h-(--available-height) min-h-0 flex-col',
        !!triggerComponent && 'w-fit min-w-0',
        contentClassName
    )
    const resolvedListClassName = cn('min-h-0 flex-1 max-h-none overflow-y-auto', listClassName)
    const handleListScroll = useCallback<NonNullable<ComboboxPrimitive.List.Props['onScroll']>>(
        (event) => {
            restListProps.onScroll?.(event)

            if (!isAsyncOptions || !canLoadMore || isQuerying || isLoadingMore) {
                return
            }

            const element = event.currentTarget as HTMLDivElement
            const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight
            if (distanceFromBottom <= DEFAULT_COMBOBOX_FIELD_LOAD_MORE_OFFSET) {
                queryMoreOptions()
            }
        },
        [canLoadMore, isAsyncOptions, isLoadingMore, isQuerying, queryMoreOptions, restListProps]
    )
    const visibleActions = (actions || []).filter((action) => action.visible !== false)
    const statusText = isHydratingValue
        ? t('components.control.combobox.loading')
        : isLoadingMore
          ? t('components.control.combobox.loadingMore')
          : isQuerying
            ? t('components.control.combobox.loading')
            : ''

    useEffect(() => {
        if (!open || !shouldShowSearchInput || !searchInputAutoFocus) {
            return
        }

        let animationFrame = 0
        let attempts = 0

        const focusSearchInput = () => {
            attempts += 1
            const input = searchInputRef.current
            if (input instanceof HTMLInputElement) {
                input.focus({ preventScroll: true })
                return
            }

            if (attempts < 8) {
                animationFrame = window.requestAnimationFrame(focusSearchInput)
            }
        }

        animationFrame = window.requestAnimationFrame(focusSearchInput)
        return () => {
            window.cancelAnimationFrame(animationFrame)
        }
    }, [open, searchInputAutoFocus, shouldShowSearchInput])

    useEffect(() => {
        if (!open || !isAsyncOptions || !canLoadMore || isQuerying || isLoadingMore) {
            return
        }

        let animationFrame = 0

        const loadMoreIfListCanStillGrow = () => {
            if (lastInitialQueryCompletedOpenGenerationRef.current !== openGenerationRef.current) {
                return
            }

            const listElement = listRef.current
            if (!listElement || listElement.clientHeight === 0) {
                return
            }

            if (autoFillLoadedCountRef.current === queriedOptionsRef.current.length) {
                return
            }

            const canScroll = listElement.scrollHeight > listElement.clientHeight + 1
            if (canScroll) {
                autoFillLoadedCountRef.current = null
                return
            }

            autoFillLoadedCountRef.current = queriedOptionsRef.current.length
            queryMoreOptions()
        }

        animationFrame = window.requestAnimationFrame(loadMoreIfListCanStillGrow)
        return () => {
            window.cancelAnimationFrame(animationFrame)
        }
    }, [canLoadMore, isAsyncOptions, isLoadingMore, isQuerying, open, queryMoreOptions, queriedOptions.length])

    return (
        <ComboboxFieldSelectionContext.Provider value={{ selectedOptions, isHydratingValue }}>
            <Combobox
                {...rootProps}
                open={open}
                disabled={isDisabled}
                multiple={multiple}
                items={resolvedRootItems}
                autoHighlight={autoHighlight}
                value={currentRootValue}
                onValueChange={handleValueChange}
                onOpenChange={handleOpenChange}
                onOpenChangeComplete={handleOpenChangeComplete}
                onInputValueChange={handleInputValueChange}
                onItemHighlighted={handleItemHighlighted}
                virtualized={virtualized}
            >
                {triggerComponent || (
                    <ComboboxPrimitive.Input
                        data-slot="combobox-input"
                        className="border-input bg-background h-8 rounded-md border px-2.5 text-sm"
                        disabled={isDisabled}
                        placeholder={isHydratingValue ? inputLoadingPlaceholder : inputPlaceholder}
                    />
                )}
                <ComboboxContent {...restContentProps} className={resolvedContentClassName}>
                    {shouldShowSearchInput && (
                        <div className="border-border bg-popover relative z-10 border-b px-2.5">
                            <div className="flex h-9 min-w-56 items-center gap-2">
                                <SearchIcon className="text-muted-foreground size-4 shrink-0" />
                                <ComboboxPrimitive.Input
                                    ref={searchInputRef}
                                    className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm outline-none"
                                    disabled={isDisabled}
                                    placeholder={resolvedSearchPlaceholder}
                                />
                            </div>
                        </div>
                    )}
                    <BaseComboboxList
                        ref={listRef}
                        {...restListProps}
                        className={resolvedListClassName}
                        onScroll={handleListScroll}
                        loading={isQuerying}
                        emptyContent={resolvedEmptyContent}
                        loadingContent={resolvedLoadingContent}
                        emptyClassName="justify-start px-2"
                    >
                        {virtualized ? (
                            <VirtualizedComboboxCollection
                                groupBy={groupBy}
                                renderOption={renderOption}
                                listRef={listRef}
                                selectedScrollValue={selectedScrollValue}
                                highlightedValue={highlightedValue}
                                highlightedReason={highlightedReason}
                            />
                        ) : groupBy ? (
                            <GroupedComboboxCollection groupBy={groupBy} renderOption={renderOption} />
                        ) : (
                            <ComboboxCollection>
                                {(item: ComboboxFieldOption) => (
                                    <ComboboxItem key={item.value} value={item} disabled={item.disabled}>
                                        {renderOption ? renderOption(item) : item.label}
                                    </ComboboxItem>
                                )}
                            </ComboboxCollection>
                        )}
                    </BaseComboboxList>
                    {isLoadingMore && (
                        <div className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm">
                            <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
                            {resolvedLoadingMoreContent}
                        </div>
                    )}
                    {visibleActions.length > 0 && (
                        <div className="border-border border-t py-0.5">
                            {visibleActions.map((action, index) => (
                                <Button
                                    key={action.key || `combobox-action-${index}`}
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        'h-9 w-full justify-center rounded-none font-normal',
                                        action.className
                                    )}
                                    disabled={action.disabled}
                                    onClick={(event) => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        action.onExecute()

                                        if (action.closeOnExecute) {
                                            const popupElement =
                                                event.currentTarget.closest('[data-slot=combobox-content]')
                                            if (popupElement instanceof HTMLElement) {
                                                popupElement.dispatchEvent(
                                                    new KeyboardEvent('keydown', {
                                                        key: 'Escape',
                                                        bubbles: true,
                                                    })
                                                )
                                            }
                                        }
                                    }}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                    <ComboboxStatus className="sr-only">{statusText}</ComboboxStatus>
                </ComboboxContent>
            </Combobox>
        </ComboboxFieldSelectionContext.Provider>
    )
}
